import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Smartphone, Shield, Clock, Mic, Wifi, Volume2, Sun, ChevronRight, Check, Calendar } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';

// Clean line icons for features
const AppControlIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <path d="M12 18h.01" />
  </svg>
);

const VoiceIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const SafetyIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ScheduleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const QuietIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

const EnergyIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

// Motion Blinds inspired benefits with custom icons
const smartFeatures = [
  { 
    icon: AppControlIcon, 
    title: 'App Control', 
    description: 'Control all your shades from anywhere using our intuitive smartphone app.' 
  },
  { 
    icon: VoiceIcon, 
    title: 'Voice Activated', 
    description: 'Works with Alexa, Google Home, Apple HomeKit, and other platforms.' 
  },
  { 
    icon: SafetyIcon, 
    title: 'Child & Pet Safe', 
    description: 'No dangerous cords or chains. Safe for your entire family.' 
  },
  { 
    icon: ScheduleIcon, 
    title: 'Smart Scheduling', 
    description: 'Automate based on time of day, sunrise/sunset, or temperature.' 
  },
  { 
    icon: QuietIcon, 
    title: 'Whisper Quiet', 
    description: 'Premium motors operate silently - less than 35dB.' 
  },
  { 
    icon: EnergyIcon, 
    title: 'Energy Efficient', 
    description: 'Reduce heat gain in summer and heat loss in winter.' 
  },
];

// Smart connectivity options with brand-specific SVG icons
const connectivityOptions = [
  { 
    name: 'Apple HomeKit', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M12 3L4 9v12h16V9l-8-6zm0 2.236L18 10v9H6v-9l6-4.764zM12 13a2 2 0 100 4 2 2 0 000-4z"/>
      </svg>
    ),
  },
  { 
    name: 'Google Home', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="12" r="4"/>
      </svg>
    ),
  },
  { 
    name: 'Amazon Alexa', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
        <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
      </svg>
    ),
  },
  { 
    name: 'Matter', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  { 
    name: 'SmartThings', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l7 3.5v7.64l-7 3.5-7-3.5V7.68l7-3.5z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    ),
  },
  { 
    name: 'Home Assistant', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3zm0 2.84L18 11v8h-2v-6H8v6H6v-8l6-5.16z"/>
      </svg>
    ),
  },
  { 
    name: 'Control4', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <rect x="3" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
  },
  { 
    name: 'Loxone', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
  },
  { 
    name: 'RTI', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <rect x="3" y="6" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
        <circle cx="8" cy="12" r="2"/>
        <line x1="13" y1="10" x2="19" y2="10" stroke="currentColor" strokeWidth="2"/>
        <line x1="13" y1="14" x2="19" y2="14" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
  },
  { 
    name: 'Bond', 
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="12" r="1.5"/>
      </svg>
    ),
  },
];

// Motor types
const motorTypes = [
  {
    name: 'Standard Roller Motor',
    description: 'For standard-size roller shades, up to 96" wide on eligible configurations. Silent operation with precise positioning.',
    specs: ['Standard sizes up to 96" wide', 'Battery or hardwired', 'Less than 35dB'],
    note: { text: 'Oversized shades available', to: '/products/motorized-shades#architectural-oversized' },
    image: '/img/catalog/roller-motor-hero-v2.jpg',
    imageAlt: 'Motorized roller shade with the roller motor and mounting bracket visible',
    productLink: '/products/roller-shades'
  },
  {
    name: 'Drapery Motor',
    description: 'Smooth, elegant operation for drapes and curtains. Ideal for large windows and tall ceilings.',
    specs: ['Up to 20ft track', 'Whisper quiet', 'Smart home ready'],
    image: '/img/catalog/drapery-motor-hero-v2.jpg',
    productLink: '/products/motorized-drapes'
  },
  {
    name: 'Roman Shade Motor',
    description: 'Precision control for Roman shades. Consistent folds and smooth operation every time.',
    specs: ['Precise folding', 'Multiple stops', 'Touch operation'],
    image: '/img/catalog/roman-motor-hero.jpg',
    productLink: '/products/roman-shades'
  },
];

// Control options with simple line icons (matching top icons style)
const controlMethods = [
  {
    title: 'Remote Control',
    description: 'Dedicated 15-channel remote for simple, reliable operation.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="2" width="12" height="20" rx="2" />
        <circle cx="12" cy="7" r="1.5" />
        <line x1="9" y1="12" x2="15" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
  },
  {
    title: 'Wall Switch',
    description: 'Elegant wall-mounted switch. Battery powered - no wiring.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="4" width="14" height="16" rx="2" />
        <circle cx="12" cy="10" r="2" />
        <line x1="12" y1="13" x2="12" y2="16" />
      </svg>
    ),
  },
  {
    title: 'Smartphone App',
    description: 'Full control from your phone. Create scenes and schedules.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M12 18h.01" />
      </svg>
    ),
  },
  {
    title: 'Voice Control',
    description: 'Hands-free with Alexa, Google Home, or Siri.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
  },
];

export const MotorizationPage = () => {
  return (
    <Layout>
      {/* Hero Section - Reduced Height */}
      <section className="relative h-[40vh] min-h-[320px] flex items-center overflow-hidden" data-testid="motorization-hero">
        <div className="absolute inset-0">
          <img 
            src="/img/catalog/motorization-hero-original.webp" 
            alt="Motion Blinds smart motor technology" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-transparent" />
        </div>
        <div className="container-xl relative z-10 pt-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
            className="max-w-xl"
          >
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Link to="/motorization/smart-connectivity" className="inline-block px-3 py-1 bg-neutral-900 text-white text-xs font-medium tracking-wider uppercase hover:bg-neutral-700 transition-colors">
                Powered by Motion Blinds
              </Link>
              <span className="inline-block px-3 py-1 bg-white border border-neutral-300 text-neutral-800 text-xs font-medium tracking-wider">
                AUTOMATE<span className="text-red-500">.</span>
              </span>
              <span className="inline-block px-3 py-1 bg-yellow-400 text-neutral-900 text-xs font-bold tracking-wider uppercase">
                Somfy
              </span>
              <span className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-medium tracking-wider uppercase">
                Lutron
              </span>
            </div>
            <h1 className="text-neutral-900 text-3xl lg:text-4xl font-serif leading-tight">
              Smart Motors.{' '}<br />
              <span className="text-neutral-600">All Shades. All Buildings.</span>
            </h1>
            <p className="mt-3 text-base text-neutral-600 leading-relaxed">
              Enter the smart home world with the most connective motorization solution.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/products">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white px-5 py-4 text-sm">
                  Explore Products
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/consultation">
                <Button variant="outline" className="border-neutral-400 text-neutral-900 hover:bg-neutral-100 px-5 py-4 text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  Free Consultation
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Smart Connectivity Ticker - Animated Marquee */}
      <section className="py-4 bg-neutral-900 overflow-hidden" data-testid="connectivity-section">
        <div className="container-xl">
          <div className="flex items-center gap-6">
            <span className="text-amber-400 text-xs font-medium tracking-wider uppercase whitespace-nowrap flex-shrink-0">Works With Everything</span>
            <div className="flex-1 overflow-hidden relative">
              {/* Gradient masks */}
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-neutral-900 to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-neutral-900 to-transparent z-10" />
              
              {/* Scrolling container */}
              <motion.div 
                className="flex gap-3"
                animate={{ x: [0, -1500] }}
                transition={{ 
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 30,
                    ease: "linear",
                  }
                }}
              >
                {/* Double the items for seamless loop */}
                {[...connectivityOptions, ...connectivityOptions].map((option, index) => (
                  <div
                    key={`${option.name}-${index}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800/50 rounded-full hover:bg-neutral-700 transition-colors cursor-pointer group flex-shrink-0"
                    title={option.name}
                  >
                    <span className="text-white/80 group-hover:text-white transition-colors">{option.icon}</span>
                    <span className="text-white/80 text-xs font-medium group-hover:text-white transition-colors whitespace-nowrap">{option.name}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Smart Motorization - Compact */}
      <section className="py-12" data-testid="benefits-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="text-amber-700 text-xs font-medium tracking-wider uppercase">Why Smart Motorization</span>
              <h2 className="mt-2 text-3xl font-serif">The Smart Choice for Modern Living</h2>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                Discover our sleek motorization solutions that not only look good but are 
                also easy to install and use.
              </p>
              
              <div className="mt-6 space-y-4">
                {smartFeatures.slice(0, 3).map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-3"
                  >
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-neutral-100 rounded-lg border border-neutral-200">
                      <feature.icon />
                    </div>
                    <div>
                      <h4 className="font-medium text-sm mb-0.5">{feature.title}</h4>
                      <p className="text-neutral-500 text-sm">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Partner Logos - Single Row */}
            <div>
              <span className="text-amber-700 text-xs font-medium tracking-wider uppercase">Our Trusted Motorization Partners for</span>
              <h3 className="mt-2 text-3xl font-serif text-neutral-800 mb-6">
                Custom Shades and Drapes
              </h3>
              {/* First Row - Motion Blinds Full Width */}
              <div className="mb-4">
                <motion.a
                  href="/motorization/smart-connectivity"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="flex items-center justify-center bg-white border border-neutral-200 rounded-xl p-6 h-32 hover:shadow-md transition-all cursor-pointer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/exyxkb2a_Powered-by-Motionblinds%20%20Logo.png" 
                    alt="Powered by Motionblinds" 
                    className="h-24 object-contain"
                  />
                </motion.a>
              </div>
              
              {/* Second Row - Automate, Somfy, Lutron */}
              <div className="grid grid-cols-3 gap-4">
                {/* Automate */}
                <motion.a
                  href="https://www.automateshades.com/us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-center bg-white border border-neutral-200 rounded-xl p-4 h-24 hover:shadow-md transition-all cursor-pointer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/cyiy3u69_image.png" 
                    alt="Automate" 
                    className="h-14 object-contain"
                  />
                </motion.a>
                
                {/* Somfy */}
                <motion.a
                  href="https://www.somfysystems.com/en-us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center justify-center bg-white border border-neutral-200 rounded-xl p-4 h-24 hover:shadow-md transition-all cursor-pointer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/5hn0d907_image.png" 
                    alt="Somfy" 
                    className="h-12 object-contain"
                  />
                </motion.a>
                
                {/* Lutron */}
                <motion.a
                  href="https://www.lutron.com/us/en/window-treatments/shades"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center justify-center bg-white border border-neutral-200 rounded-xl p-4 h-24 hover:shadow-md transition-all cursor-pointer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ylx0vipe_image.png" 
                    alt="Lutron" 
                    className="h-14 object-contain"
                  />
                </motion.a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More Benefits Grid - Compact */}
      <section className="py-10 bg-neutral-50">
        <div className="container-xl">
          <div className="grid md:grid-cols-3 gap-4">
            {smartFeatures.slice(3).map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-5 rounded-lg border border-neutral-200"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-neutral-100 rounded-lg border border-neutral-200 mb-3">
                  <feature.icon />
                </div>
                <h4 className="font-medium text-base mb-1">{feature.title}</h4>
                <p className="text-neutral-500 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Motor Types / Products - Compact */}
      <section className="py-12" data-testid="motors-section">
        <div className="container-xl">
          <div className="text-center mb-10">
            <span className="text-amber-700 text-xs font-medium tracking-wider uppercase">Our Smart Motors</span>
            <h2 className="mt-2 text-3xl font-serif">Designed for All Blinds and Shades</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {motorTypes.map((motor, index) => (
              <motion.div
                key={motor.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white border border-neutral-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img 
                    src={motor.image} 
                    alt={motor.imageAlt || motor.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-medium text-lg mb-1">{motor.name}</h4>
                  <p className="text-neutral-500 text-sm mb-3">{motor.description}</p>
                  <ul className="space-y-1 mb-4">
                    {motor.specs.map((spec) => (
                      <li key={spec} className="flex items-center gap-2 text-xs text-neutral-600">
                        <Check className="w-3 h-3 text-green-500" />
                        {spec}
                      </li>
                    ))}
                  </ul>
                  {motor.note && <Link to={motor.note.to} className="mb-4 inline-block text-xs font-medium text-amber-700 underline underline-offset-2">{motor.note.text}</Link>}
                  <Link to={motor.productLink}>
                    <Button variant="outline" size="sm" className="w-full group-hover:bg-neutral-900 group-hover:text-white transition-colors text-sm">
                      Configure Now
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Control Methods - Compact with Simple Icons */}
      <section className="py-10 bg-neutral-900" data-testid="controls-section">
        <div className="container-xl">
          <div className="text-center mb-6">
            <span className="text-amber-400 text-xs font-medium tracking-wider uppercase">Easy to Use</span>
            <h2 className="mt-1 text-white text-xl">Choose Your Control Method</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {controlMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-neutral-800/50 p-4 rounded-lg text-center hover:bg-neutral-800 transition-colors"
              >
                <div className="w-10 h-10 mx-auto mb-2 bg-neutral-100 rounded-lg flex items-center justify-center text-neutral-900">
                  {method.icon}
                </div>
                <h4 className="font-medium text-white text-sm mb-1">{method.title}</h4>
                <p className="text-neutral-400 text-xs leading-relaxed">{method.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specs Banner - Compact */}
      <section className="py-8 bg-amber-600">
        <div className="container-xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <p className="text-3xl font-bold mb-1">&lt;35dB</p>
              <p className="text-amber-100 text-sm">Whisper Quiet</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-1">App + Voice</p>
              <p className="text-amber-100 text-sm">Smart Control</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-1">1000+</p>
              <p className="text-amber-100 text-sm">Cycles per Charge</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-1">~150"</p>
              <p className="text-amber-100 text-sm">Oversized Width, Eligible Systems</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-12 bg-neutral-50" data-testid="motorization-cta">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-amber-700 text-xs font-medium tracking-wider uppercase">Get Smart</span>
              <h2 className="mt-2 text-3xl font-serif">Ready to Go Motorized?</h2>
              <p className="mt-3 text-neutral-600 leading-relaxed">
                Let our team help you choose the right motorization solution for your project.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/contact?consultation=true">
                  <Button className="bg-neutral-900 hover:bg-neutral-800 text-white px-5">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Consultation
                  </Button>
                </Link>
                <Link to="/quote">
                  <Button variant="outline" className="px-5">
                    Get a Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-medium mb-4">What's Included</h3>
              <ul className="space-y-2">
                {[
                  'Free in-home consultation',
                  'Professional measurement',
                  'Custom-made to your measurements',
                  'Expert installation',
                  'Smart home integration setup',
                  'Manufacturer-backed motor warranty',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
